import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useLayoutEffect, useState, useRef, useEffect, useCallback} from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Node,
  XYPosition,
  useEdgesState,
  useNodesState,
  MarkerType,
  ReactFlowInstance,
} from 'reactflow';

import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import {
  StudyInaccessibleContentBanner,
  StudyQueryErrorBanner,
} from 'studies/typed/utils/ui/StudyQueryBanners';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar, {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {assertNever} from 'util/Types';

import UnitOperationFlowchartNode, {NodeType} from './UnitOperationFlowchartNode';
import {getMethodId} from './UnitOpLinkingFlowchartUtils';
import {
  getDestinationNodeId,
  getOrderedDestPMEIsInColumnsPerProcedureRun,
  makeDestinationPMEINode,
  makeIntraRunMethodEdges,
  makeUnitOpLinkEdge,
  getNodePositionsByPMEIIdForProcedureRun,
  NODE_HORIZONTAL_GAP,
  PROCEDURE_RUN_HORIZONTAL_GAP,
  UnitOpLinkEdge,
} from './UnitOpLinkingFlowchartUtils';
import UnitOpLinkingSelectTree from './UnitOpLinkingSelectTree';
import {UnitOperationFields, UnitOpLinkingStateStatus} from './useUnitOpLinkingState';
import useUnitOpLinkingStudyState from './useUnitOpLinkingStudyState';

const LEFT_SIDEPANEL_SIZE = SidepanelSize.SMALL;
const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;

const NODE_TYPES = {
  [NodeType.DESTINATION_PMEI]: UnitOperationFlowchartNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 100, y: 100, zoom: 1},
  minZoom: 0.25,
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

const NODE_GROUP_HOVER_STYLE = {
  width: 200,
  height: 100,
  backgroundColor: Color.GRAY_100,
  border: `2px solid ${Color.GRAY_900}`,
  borderRadius: '8px',
};

const NODE_GROUP_SELECTION_STYLE = {
  width: 200,
  height: 100,
  backgroundColor: Color.BLUE_100,
  border: `2px solid ${Color.BLUE_500}`,
  borderRadius: '8px',
};

interface UnitOpLinkingFlowchartProps {
  studyId: string;
}

export default function QueryingUnitOpLinkingStudyFlowchart(props: UnitOpLinkingFlowchartProps): JSX.Element {
  const unitOpLinkingState = useUnitOpLinkingStudyState(props.studyId);
  if (unitOpLinkingState.status === UnitOpLinkingStateStatus.QUERY_ERROR) {
    return <StudyQueryErrorBanner />;
  } else if (unitOpLinkingState.status === UnitOpLinkingStateStatus.QUERY_PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (unitOpLinkingState.status === UnitOpLinkingStateStatus.STUDY_INACCESSIBLE) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={unitOpLinkingState.inaccessibleStudy}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
      />
    );
  } else if (unitOpLinkingState.status === UnitOpLinkingStateStatus.SUCCESS) {
    return (
      <UnitOpLinkingStudyFlowchart
        unitOperationNodes={unitOpLinkingState.pmeis}
        interRunLinks={unitOpLinkingState.interProcedureLinks}
      />
    );
  } else {
    assertAndContinue(false, `Unexpected status ${assertNever(unitOpLinkingState.status)}`);
    return <></>;
  }
}

export function UnitOpLinkingStudyFlowchart({
  unitOperationNodes,
  interRunLinks,
}: {
  unitOperationNodes: Array<UnitOperationFields>;
  interRunLinks: Array<UnitOpLinkEdge>;
}): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <UnitOpLinkingStudyFlowchartPresentational
          unitOperationNodes={unitOperationNodes}
          interRunLinks={interRunLinks}
        />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

export function UnitOpLinkingStudyFlowchartPresentational({
  unitOperationNodes,
  interRunLinks,
}: {
  unitOperationNodes: Array<UnitOperationFields>;
  interRunLinks: Array<UnitOpLinkEdge>;
}): JSX.Element {
  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(true);
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(0);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [hoveringItemId, setHoveringItemId] = useState<string | null>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const orderedPMEIsInColumnsPerProcedureRun = useMemo(
    () => getOrderedDestPMEIsInColumnsPerProcedureRun(unitOperationNodes),
    [unitOperationNodes]
  );

  // Pan to selected nodes when selection changes
  useEffect(() => {
    if (reactFlowInstance.current && selectedIds.length > 0) {
      // Get the node IDs that correspond to the selected items
      const nodeIdsToFit: Array<string> = [];

      for (const selectedId of selectedIds) {
        // Check if selected item is a PMEI (individual node)
        const pmeiNode = unitOperationNodes.find((pmei) => pmei.pmeiId === selectedId);
        if (pmeiNode) {
          nodeIdsToFit.push(getDestinationNodeId(pmeiNode.pmeiId));
          continue;
        }

        // Check if selected item is a method group
        const methodPMEIs = unitOperationNodes.filter(
          (pmei) => getMethodId(pmei.procedureRunId, pmei.methodName) === selectedId
        );
        if (methodPMEIs.length > 0) {
          nodeIdsToFit.push(...methodPMEIs.map((pmei) => getDestinationNodeId(pmei.pmeiId)));
          continue;
        }

        // Check if selected item is a procedure run
        const procedureRunPMEIs = unitOperationNodes.filter((pmei) => pmei.procedureRunId === selectedId);
        if (procedureRunPMEIs.length > 0) {
          nodeIdsToFit.push(...procedureRunPMEIs.map((pmei) => getDestinationNodeId(pmei.pmeiId)));
        }
      }

      if (nodeIdsToFit.length > 0) {
        if (reactFlowInstance.current) {
          reactFlowInstance.current.fitView({
            nodes: nodeIdsToFit.map((id) => ({id})),
            padding: 0.2,
            duration: 800,
            includeHiddenNodes: false,
          });
        }
      }
    }
  }, [selectedIds, unitOperationNodes, isLeftSidepanelOpen]);

  const getGroupNodeId = useCallback(
    (prefix: string, hoveringId: string): string | null => {
      const pmeiNode = unitOperationNodes.find(
        (pmei) =>
          pmei.pmeiId === hoveringId ||
          pmei.procedureRunId === hoveringId ||
          getMethodId(pmei.procedureRunId, pmei.methodName) === hoveringId
      );
      return pmeiNode ? `${prefix}-${hoveringId}` : null;
    },
    [unitOperationNodes]
  );

  // Helper function to create group nodes
  const createGroupNodes = useCallback(
    (hoveringId: string | null, selectedIds: Array<string>): Array<Node> => {
      const groups: Array<Node> = [];

      // Create hover group if hovering over a node that is not selected
      const groupId =
        hoveringId && !selectedIds.includes(hoveringId) ? getGroupNodeId('hover-group', hoveringId) : null;
      if (groupId) {
        groups.push({
          id: groupId,
          type: 'group',
          position: {x: 0, y: 0}, // Will be calculated based on child node position
          style: NODE_GROUP_HOVER_STYLE,
          data: {},
        });
      }

      // Create selection groups
      for (const selectedId of selectedIds) {
        const groupId = getGroupNodeId('selection-group', selectedId);
        if (groupId) {
          groups.push({
            id: groupId,
            type: 'group',
            position: {x: 0, y: 0},
            style: NODE_GROUP_SELECTION_STYLE,
            data: {},
          });
        }
      }

      return groups;
    },
    [getGroupNodeId]
  );

  const [edges] = useEdgesState<UnitOpLinkEdge>([
    ...orderedPMEIsInColumnsPerProcedureRun.flatMap((procedureRun) =>
      makeIntraRunMethodEdges(procedureRun).map((edge) => ({
        ...edge,
        markerEnd: {type: MarkerType.Arrow, strokeWidth: 1.5},
        style: {
          strokeWidth: 3,
        },
      }))
    ),
    ...interRunLinks.map((link) => ({
      ...makeUnitOpLinkEdge(link),
      source: getDestinationNodeId(link.sourcePMEIId),
      markerEnd: {type: MarkerType.Arrow, color: Color.FUCHSIA_400, strokeWidth: 1.5},
      style: {
        stroke: Color.FUCHSIA_400,
        strokeDasharray: '5, 5',
        strokeWidth: 3,
      },
    })),
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState([
    ...unitOperationNodes.map((node) => makeDestinationPMEINode(node, {selectable: false})),
  ]);

  // Detect if node membership (add/remove) or dimensions (populated by reactflow) have changed
  const nodeLayoutDepsHash = JSON.stringify(
    nodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width}))
  );

  // Calculate positions for the nodes.
  useLayoutEffect(
    () => {
      // Calculate positions for each procedure run, starting from the left
      let currentX = 0;
      const positionsByPMEIId: Record<string, XYPosition> = {};

      for (const procedureRun of orderedPMEIsInColumnsPerProcedureRun) {
        // Get nodes for this procedure run
        const procedureRunPMEIIds = new Set(
          procedureRun.flatMap((methodColumn) => methodColumn.map((pmei) => pmei.pmeiId))
        );
        const procedureRunNodes = nodes.filter((node) => procedureRunPMEIIds.has(node.data.pmeiId));

        // Calculate positions for this procedure run
        const {positionsByPMEIId: positionsByPMEIIdForProcedureRun} = getNodePositionsByPMEIIdForProcedureRun(
          procedureRunNodes,
          procedureRun,
          currentX
        );

        // Merge positions
        Object.assign(positionsByPMEIId, positionsByPMEIIdForProcedureRun);

        // Calculate the width of this procedure run to determine starting X for next run
        const procedureRunWidth = getProcedureRunWidth(procedureRunNodes, procedureRun);
        currentX += procedureRunWidth + PROCEDURE_RUN_HORIZONTAL_GAP;
      }

      // Create and update group node positions based on their children
      const groupNodes = createGroupNodes(hoveringItemId, selectedIds);
      const updatedGroupNodes = groupNodes.map((groupNode: Node) => {
        // Get all nodes that belong to this group based on the group ID
        let groupNodeIds: Array<string> = [];

        if (groupNode.id.startsWith('hover-group-') || groupNode.id.startsWith('selection-group-')) {
          // Remove the prefix from the group ID to get the ID of the node it represents
          const groupId = groupNode.id.replace('hover-group-', '').replace('selection-group-', '');

          // Check what type of node the group represents
          const pmeiNode = unitOperationNodes.find((pmei) => pmei.pmeiId === groupId);
          const methodPMEIs = unitOperationNodes.filter(
            (pmei) => getMethodId(pmei.procedureRunId, pmei.methodName) === groupId
          );
          const procedureRunPMEIs = unitOperationNodes.filter((pmei) => pmei.procedureRunId === groupId);
          if (pmeiNode) {
            groupNodeIds = [pmeiNode.pmeiId];
          } else if (methodPMEIs.length > 0) {
            groupNodeIds = methodPMEIs.map((pmei) => pmei.pmeiId);
          } else if (procedureRunPMEIs.length > 0) {
            groupNodeIds = procedureRunPMEIs.map((pmei) => pmei.pmeiId);
          }
        }

        if (groupNodeIds.length === 0) {
          return groupNode;
        }

        // Calculate bounding box based on all nodes in the group
        const groupNodes = nodes.filter((node) => groupNodeIds.includes(node.data.pmeiId));
        const minX = Math.min(...groupNodes.map((node) => positionsByPMEIId[node.data.pmeiId]?.x || 0));
        const maxX = Math.max(
          ...groupNodes.map((node) => (positionsByPMEIId[node.data.pmeiId]?.x || 0) + (node.width || 0))
        );
        const minY = Math.min(...groupNodes.map((node) => positionsByPMEIId[node.data.pmeiId]?.y || 0));
        const maxY = Math.max(
          ...groupNodes.map((node) => (positionsByPMEIId[node.data.pmeiId]?.y || 0) + (node.height || 0))
        );

        return {
          ...groupNode,
          position: {x: minX - 10, y: minY - 10},
          style: {
            ...groupNode.style,
            width: maxX - minX + 20,
            height: maxY - minY + 20,
          },
        };
      });

      setNodes((prevNodes) => {
        const nonGroupNodes = prevNodes.filter((node) => node.type !== 'group');
        // Sort nodes so that group nodes (parents) come before their children
        const sortedNodes = [
          ...updatedGroupNodes.sort(sortGroupNodes),
          ...nonGroupNodes.map((node) => ({
            ...node,
            position: positionsByPMEIId[node.data.pmeiId] || {x: 0, y: 0},
          })),
        ];

        return sortedNodes;
      });
    },
    // Can't use nodes as dependency because the reference changes every time state is set,
    // resulting in an infinite loop. Instead use nodeLayoutDepsHash to only detect changes we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitOperationNodes, nodeLayoutDepsHash, hoveringItemId, selectedIds]
  );

  return (
    <div className={css(styles.fullScreenContainer)}>
      <div className={css(styles.leftSidepanelContainer)}>
        <FlowchartSidebar
          icons={[
            {
              name: 'multi-select',
              onClick: () => {
                setIsLeftSidepanelOpen(true);
              },
              tooltip: 'Overview',
            },
          ]}
          selectedIconIndex={selectedSidebarIconIndex}
          setSelectedIconIndex={setSelectedSidebarIconIndex}
        />
        <FlowchartContextPanel
          panelSide={PanelSide.LEFT}
          sidepanelName="Overview"
          isOpen={isLeftSidepanelOpen}
          onClose={() => {
            setIsLeftSidepanelOpen(false);
            setSelectedSidebarIconIndex(null);
          }}
          size={SidepanelSize.SMALL}
          showExpandCollapseButtons={false}
        >
          <div className={css(styles.selectTreeContainer)}>
            <UnitOpLinkingSelectTree
              allPMEIs={unitOperationNodes}
              setHoveringItemId={setHoveringItemId}
              selectedIds={selectedIds}
              setSelectedIds={(ids) => {
                if (selectedIds.includes(ids[0])) {
                  setSelectedIds([]);
                } else {
                  setSelectedIds(ids);
                }
              }}
            />
          </div>
        </FlowchartContextPanel>
      </div>
      <div
        style={{
          flexGrow: 1,
          marginLeft: isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0,
          position: 'relative',
        }}
      >
        <ReactFlow
          nodes={nodes}
          onNodesChange={onNodesChange}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onInit={(instance) => {
            reactFlowInstance.current = instance;
          }}
          {...STATIC_PROPS}
        >
          <HorizontalControls shiftLeft={FLOWCHART_SIDE_PANEL_WIDTH} />
          <DefaultBackground />
        </ReactFlow>
      </div>
    </div>
  );
}

/**
 * Calculate the total width of a procedure run including all its method columns and gaps.
 */
function getProcedureRunWidth(
  procedureRunNodes: Array<Node>,
  procedureRun: Array<Array<UnitOperationFields>>
): number {
  const nodeByPMEIId = new Map(procedureRunNodes.map((node) => [node.data.pmeiId, node]));
  let totalWidth = 0;

  for (let i = 0; i < procedureRun.length; i++) {
    const methodColumn = procedureRun[i];
    const maxNodeWidth = Math.max(...methodColumn.map((pmei) => nodeByPMEIId.get(pmei.pmeiId)?.width ?? 0));
    totalWidth += maxNodeWidth;

    // Add gap between method columns (except for the last one)
    if (i < procedureRun.length - 1) {
      totalWidth += NODE_HORIZONTAL_GAP;
    }
  }

  return totalWidth;
}

/**
 * Sort group nodes so that hover groups come after selection groups
 */
function sortGroupNodes(a: Node, b: Node): number {
  const aIsHover = a.id.startsWith('hover-group-');
  const bIsHover = b.id.startsWith('hover-group-');
  if (aIsHover && !bIsHover) {
    return 1;
  }
  if (!aIsHover && bIsHover) {
    return -1;
  }
  return 0;
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    margin: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  noUserSelect: {
    userSelect: 'none',
  },
  loadingDiv: {
    paddingBottom: 5,
  },
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    // Ensure left sidepanel renders on top of reactflow canvas
    // Instead of shifting reactflow canvas when left sidepanel is open
    zIndex: 10,
  },
  selectTreeContainer: {
    height: '100%',
  },
});
